import { useState, useRef, useEffect } from 'react'
import { Edit3 } from 'lucide-react'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { Label } from '../ui/label'
import { labelStyle, valueStyle } from '../../styles/typography'

type FieldType = 'text' | 'textarea'

interface InlineEditFieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  type?: FieldType
  placeholder?: string
  required?: boolean
  error?: boolean
  rows?: number // For textarea
  className?: string
  displayClassName?: string
  alwaysEditing?: boolean // If true, always show input instead of click-to-edit
}

export function InlineEditField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder = '',
  required = false,
  error = false,
  rows = 3,
  className = '',
  displayClassName = '',
  alwaysEditing = false
}: InlineEditFieldProps) {
  const [isEditing, setIsEditing] = useState(alwaysEditing)
  const [internalValue, setInternalValue] = useState(value)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  // Sync internal value when prop changes
  useEffect(() => {
    setInternalValue(value)
  }, [value])

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isEditing])

  const handleClick = () => {
    if (!alwaysEditing) {
      setIsEditing(true)
    }
  }

  const handleBlur = () => {
    if (!alwaysEditing) {
      setIsEditing(false)
      onChange(internalValue)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInternalValue(e.target.value)
    if (alwaysEditing) {
      onChange(e.target.value)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && type === 'text' && !alwaysEditing) {
      setIsEditing(false)
      onChange(internalValue)
    }
    if (e.key === 'Escape' && !alwaysEditing) {
      setInternalValue(value)
      setIsEditing(false)
    }
  }

  const errorClasses = error ? 'border-red-500 focus:border-red-500' : ''

  if (isEditing || alwaysEditing) {
    return (
      <div className={className}>
        <Label style={labelStyle}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        {type === 'textarea' ? (
          <Textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={internalValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={rows}
            className={`mt-1 ${errorClasses}`}
          />
        ) : (
          <Input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            value={internalValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={`mt-1 ${errorClasses}`}
          />
        )}
      </div>
    )
  }

  // Display mode (click to edit)
  return (
    <div className={`group cursor-pointer ${className}`} onClick={handleClick}>
      <Label style={labelStyle} className="flex items-center gap-2">
        {label}
        {required && <span className="text-red-500">*</span>}
      </Label>
      <div className={`mt-1 flex items-start gap-2 ${displayClassName}`}>
        {type === 'textarea' ? (
          <p
            className={`flex-1 text-gray-700 ${!value ? 'text-gray-400 italic' : ''} ${error ? 'text-red-500' : ''}`}
            style={valueStyle}
          >
            {value || placeholder || 'Click to edit...'}
          </p>
        ) : (
          <span
            className={`flex-1 text-gray-700 ${!value ? 'text-gray-400 italic' : ''} ${error ? 'text-red-500' : ''}`}
            style={valueStyle}
          >
            {value || placeholder || 'Click to edit...'}
          </span>
        )}
        <Edit3 className="h-3.5 w-3.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" />
      </div>
    </div>
  )
}
